import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityRecord,
  IGetRecordResponse,
  IMigratedDataSummary,
  IPracticeMigration,
  ISourceEntityRecord,
  SkippedDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import { PatientRelationship } from '@principle-theorem/principle-core';
import {
  IBasePatient,
  IHasPrimaryContact,
  IPatient,
  IsPrimaryContact,
  PatientRelationshipType,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  FirestoreMigrate,
  PrimaryKey,
  WithRef,
  asDocRef,
  getError,
  isSameRef,
  toNamedDocument,
  toTimestamp,
} from '@principle-theorem/shared';
import { Observable, from, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { DestinationEntity } from '../../../destination/destination-entity';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientRelationshipDestinationEntity,
  IPatientRelationshipJobData as IPatientRelationshipBaseJobData,
} from '../../../destination/entities/patient-relationship';
import { TranslationMapHandler } from '../../../translation-map';
import {
  OasisPatient,
  PatientSourceEntity,
} from '../../source/entities/patients';
import { PatientDestinationEntity } from './patients';

export const PATIENT_RELATIONSHIP_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: 'patientRelationships',
    label: 'Patient Relationships',
    description: '',
  },
});

export interface IPatientRelationshipDestinationRecord {
  sourceRef: PrimaryKey<ISourceEntityRecord>;
  primaryContactRef: DocumentReference<IPatient>;
}

export type IPatientRelationshipJobData =
  IPatientRelationshipBaseJobData<OasisPatient>;

export interface IPatientMigrationData {
  sourcePatientId: string;
  patientRef: DocumentReference<IPatient>;
  primaryContact: IHasPrimaryContact & {
    patientRef: DocumentReference<IPatient>;
  };
}

export class PatientRelationshipDestinationEntity extends BasePatientRelationshipDestinationEntity<
  OasisPatient,
  IPatientRelationshipDestinationRecord,
  IPatientMigrationData
> {
  destinationEntity = PATIENT_RELATIONSHIP_DESTINATION_ENTITY;

  patientSourceEntity = new PatientSourceEntity();
  sourceCountComparison = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  getMigratedData$(
    record: IDestinationEntityRecord<IPatientRelationshipDestinationRecord>
  ): Observable<IMigratedDataSummary[]> {
    if (record.status !== DestinationEntityRecordStatus.Migrated) {
      return of([]);
    }

    return from(Firestore.getDoc(record.data.primaryContactRef)).pipe(
      map((patient) => [
        {
          label: 'Primary Patient',
          data: patient,
        },
      ])
    );
  }

  async buildMigrationData(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientRelationshipJobData
  ): Promise<
    | IPatientMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
    | (IDestinationEntityRecord & SkippedDestinationEntityRecord)
  > {
    const sourcePatientId = this.sourceEntities.patients
      .getSourceRecordId(data.sourcePatient.data)
      .toString();

    const accountPatientId =
      data.sourcePatient.data.accountPatientId.toString();
    if (accountPatientId === sourcePatientId) {
      return this.buildSkippedResponse(data);
    }

    try {
      const sourcePatientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId,
        PATIENT_RESOURCE_TYPE
      );
      const accountPatientRef = await translationMap.getDestination<IPatient>(
        accountPatientId,
        PATIENT_RESOURCE_TYPE
      );

      if (!sourcePatientRef) {
        throw new Error(
          `Patient relation can't be found for id ${sourcePatientId}`
        );
      }

      if (!accountPatientRef) {
        // eslint-disable-next-line no-console
        console.warn(
          `Account patient record not found for id ${accountPatientId}. SKipping.`
        );
        return this.buildSkippedResponse(data);
      }

      if (isSameRef(sourcePatientRef, accountPatientRef)) {
        throw new Error(`Source and account patients are the same`);
      }

      return {
        sourcePatientId,
        patientRef: sourcePatientRef,
        primaryContact: {
          patientRef: accountPatientRef,
          primaryContact: {
            patient: toNamedDocument(
              await Firestore.getDoc(
                asDocRef<IBasePatient & IsPrimaryContact>(accountPatientRef)
              )
            ),
            type: PatientRelationshipType.Unknown,
          },
        },
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }

  async runJob(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMapHandler: TranslationMapHandler,
    jobData: IPatientRelationshipJobData,
    migrationData: IPatientMigrationData
  ): Promise<IDestinationEntityRecord> {
    try {
      await FirestoreMigrate.patchDoc(migrationData.patientRef, {
        primaryContact: migrationData.primaryContact.primaryContact,
      });

      await PatientRelationship.addPrimaryRelationship(
        await Firestore.getDoc(migrationData.patientRef)
      );

      return this._buildSuccessResponse(
        jobData.sourcePatient.record,
        migrationData.primaryContact.patientRef
      );
    } catch (error) {
      return this._buildErrorResponse(
        jobData.sourcePatient.record,
        getError(error)
      );
    }
  }

  private _buildSuccessResponse(
    sourcePatient: Pick<
      IGetRecordResponse<OasisPatient>['record'],
      'label' | 'uid' | 'id'
    >,
    primaryContactRef: DocumentReference<IPatient>
  ): IDestinationEntityRecord<IPatientRelationshipDestinationRecord> {
    return {
      uid: sourcePatient.uid,
      label: sourcePatient.label,
      data: {
        sourceRef: sourcePatient.id,
        primaryContactRef,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: sourcePatient.id,
      migratedAt: toTimestamp(),
    };
  }
}
